/**
 * KET Academy Role-Based Access Control System
 *
 * This file defines a simplified role-based access control system with only two core roles:
 * - STUDENT: Regular students with basic access to resources and courses
 * - AMBASSADOR: Teachers/educators/professionals with content creation and mentoring capabilities
 *
 * This simplified system provides a clear distinction between learners and educators,
 * making the platform easier to understand and maintain while still providing
 * appropriate access controls for different user types.
 */

// Core role definitions - simplified to only two roles
export const CORE_ROLES = {
  STUDENT: 'STUDENT', // Regular student with basic access
  AMBASSADOR: 'AMBASSADOR', // Teacher/educator/professional with advanced capabilities
}

// Map detailed user statuses to core roles
export const mapStatusToRole = (status) => {
  // All new users default to STUDENT - ambassadors are promoted by admins
  if (!status) {
    console.warn('mapStatusToRole: Null/undefined status provided')
    return CORE_ROLES.STUDENT
  }

  // For now, everyone starts as STUDENT regardless of status
  // Ambassadors must be manually promoted by administrators
  return CORE_ROLES.STUDENT
}

// Permission definitions - streamlined for simplified role system
export const PERMISSIONS = {
  // Content access
  VIEW_PUBLIC_RESOURCES: 'view_public_resources',
  VIEW_PREMIUM_RESOURCES: 'view_premium_resources',
  DOWNLOAD_RESOURCES: 'download_resources',

  // Content management (Ambassador only)
  CREATE_RESOURCES: 'create_resources',
  EDIT_RESOURCES: 'edit_resources',
  DELETE_RESOURCES: 'delete_resources',
  APPROVE_CONTENT: 'approve_content',

  // Course management
  CREATE_COURSES: 'create_courses',
  EDIT_COURSES: 'edit_courses',
  DELETE_COURSES: 'delete_courses',
  ENROLL_COURSES: 'enroll_courses',

  // User management (Ambassador only)
  VIEW_USER_PROFILES: 'view_user_profiles',
  EDIT_USER_PROFILES: 'edit_user_profiles',
  MANAGE_USERS: 'manage_users',

  // Analytics
  VIEW_PERSONAL_ANALYTICS: 'view_personal_analytics',
  VIEW_REGIONAL_ANALYTICS: 'view_regional_analytics',
  VIEW_REPORTS: 'view_reports',

  // Communication
  USE_CHAT_SYSTEM: 'use_chat_system',
  MODERATE_DISCUSSIONS: 'moderate_discussions',

  // Events
  CREATE_EVENTS: 'create_events',
  MANAGE_EVENTS: 'manage_events',

  // Mentoring (Ambassador only)
  MENTOR_STUDENTS: 'mentor_students',

  // Advanced features
  USE_AI_TOOLS: 'use_ai_tools',

  // System management (Ambassador only)
  MANAGE_SYSTEM: 'manage_system',
}

// Create an array of all available permissions for use in the PermissionsExplorer
export const ALL_PERMISSIONS = Object.values(PERMISSIONS)

// Role-based permission assignments - updated for three roles
export const rolePermissions = {
  [CORE_ROLES.STUDENT]: [
    // Basic content access
    PERMISSIONS.VIEW_PUBLIC_RESOURCES,
    PERMISSIONS.DOWNLOAD_RESOURCES,
    PERMISSIONS.ENROLL_COURSES,

    // Personal analytics
    PERMISSIONS.VIEW_PERSONAL_ANALYTICS,

    // Communication
    PERMISSIONS.USE_CHAT_SYSTEM,

    // Advanced features
    PERMISSIONS.USE_AI_TOOLS,
  ],

  [CORE_ROLES.AMBASSADOR]: [
    // All student permissions plus advanced capabilities
    PERMISSIONS.VIEW_PUBLIC_RESOURCES,
    PERMISSIONS.VIEW_PREMIUM_RESOURCES,
    PERMISSIONS.DOWNLOAD_RESOURCES,

    // Content management
    PERMISSIONS.CREATE_RESOURCES,
    PERMISSIONS.EDIT_RESOURCES,
    PERMISSIONS.DELETE_RESOURCES,
    PERMISSIONS.APPROVE_CONTENT,

    // Course management
    PERMISSIONS.CREATE_COURSES,
    PERMISSIONS.EDIT_COURSES,
    PERMISSIONS.DELETE_COURSES,
    PERMISSIONS.ENROLL_COURSES,

    // User management
    PERMISSIONS.VIEW_USER_PROFILES,
    PERMISSIONS.EDIT_USER_PROFILES,
    PERMISSIONS.MANAGE_USERS,

    // Analytics
    PERMISSIONS.VIEW_PERSONAL_ANALYTICS,
    PERMISSIONS.VIEW_REGIONAL_ANALYTICS,
    PERMISSIONS.VIEW_REPORTS,

    // Communication
    PERMISSIONS.USE_CHAT_SYSTEM,
    PERMISSIONS.MODERATE_DISCUSSIONS,

    // Events
    PERMISSIONS.CREATE_EVENTS,
    PERMISSIONS.MANAGE_EVENTS,

    // Mentoring
    PERMISSIONS.MENTOR_STUDENTS,

    // Advanced features
    PERMISSIONS.USE_AI_TOOLS,

    // System management
    PERMISSIONS.MANAGE_SYSTEM,
  ],

  [CORE_ROLES.ADMINISTRATOR]: [
    // All permissions
    ...ALL_PERMISSIONS,
  ],
}

/**
 * Check if a user has a specific permission
 * @param {string} userStatus - The user's status (e.g., 'STUDENT', 'AMBASSADOR')
 * @param {string} permission - The permission to check
 * @returns {boolean} - Whether the user has the permission
 */
export const hasPermission = (userStatus, permission) => {
  const role = mapStatusToRole(userStatus)
  return rolePermissions[role]?.includes(permission) || false
}

/**
 * Get all permissions for a specific role
 * @param {string} role - The role to get permissions for
 * @returns {Array} - Array of permissions for the role
 */
export const getPermissionsForRole = (role) => {
  return rolePermissions[role] || []
}

/**
 * Check if a user status is allowed to transition to another status
 * @param {string} currentStatus - Current user status
 * @param {string} targetStatus - Target user status
 * @returns {boolean} - Whether the transition is allowed
 */
export const canTransitionToStatus = (currentStatus, targetStatus) => {
  const currentRole = mapStatusToRole(currentStatus)
  const targetRole = mapStatusToRole(targetStatus)

  // Define valid transitions - simplified for two roles
  const validTransitions = {
    [CORE_ROLES.STUDENT]: [
      CORE_ROLES.AMBASSADOR, // Students can become ambassadors
    ],
    [CORE_ROLES.AMBASSADOR]: [], // Ambassadors can't transition upward
  }

  return validTransitions[currentRole]?.includes(targetRole) || false
}
